import { toRouteTemplate } from './routeTemplate';
import { ELEMENT_NAME_CLASS_PREFIX } from './sensitive';

export type BeforeTrackOptions = {
  environment: string;
  applicationName: string;
  getPathname: () => string;
  getOrigin: () => string;
};

const stripSearch = (url: string, currentOrigin: string): string => {
  if (url.startsWith('/')) return url.split('?')[0];

  try {
    const parsed = new URL(url);
    return parsed.origin === currentOrigin ? `${parsed.origin}${parsed.pathname}` : url;
  } catch {
    return url;
  }
};

// `$elements` lists the clicked element first, then each ancestor up to
// `<body>`, so the first named one is the nearest.
const findElementName = (elements: unknown): string | undefined => {
  if (!Array.isArray(elements)) return undefined;

  for (const element of elements) {
    const classes: unknown = (element as { $classes?: unknown } | null)?.$classes;
    if (!Array.isArray(classes)) continue;

    const named = classes.find(
      (className): className is string =>
        typeof className === 'string' && className.startsWith(ELEMENT_NAME_CLASS_PREFIX)
    );
    if (named) return named.slice(ELEMENT_NAME_CLASS_PREFIX.length);
  }

  return undefined;
};

/**
 * Builds the hook that stamps `application_name`, `environment` and `page`
 * onto every captured event, and strips the query string out of every
 * URL-shaped property Mixpanel adds on its own — `$current_url`, `$pathname`,
 * `current_url_path`/`current_url_search`, `$el_attr__href` — all four of
 * which Mixpanel populates straight from `window.location`/the clicked
 * element, independent of autocapture's own text/attribute settings.
 *
 * Also copies the nearest `analyticsName` class into a flat `element_name`, so
 * clicks on a control stay reportable even when it is marked sensitive.
 */
export const buildBeforeTrack =
  ({ applicationName, environment, getOrigin, getPathname }: BeforeTrackOptions) =>
  (eventName: string, properties: Record<string, unknown>): [string, Record<string, unknown>] => {
    const sanitized = { ...properties };
    const { $el_attr__href } = sanitized;

    delete sanitized.current_url_search;

    if (typeof $el_attr__href === 'string') {
      sanitized.$el_attr__href = stripSearch($el_attr__href, getOrigin());
    }

    const elementName = findElementName(sanitized.$elements);
    if (elementName !== undefined) {
      sanitized.element_name = elementName;
    }

    return [
      eventName,
      {
        ...sanitized,
        application_name: applicationName,
        environment,
        page: toRouteTemplate(getPathname()),
        $current_url: `${getOrigin()}${getPathname()}`,
      },
    ];
  };
